import React from "react";
import { observer } from "mobx-react";
import { computed, observable, action } from "mobx";
import { FormSpy } from "react-final-form";
import { Button, Typography } from "@material-ui/core";
import InAppStudioStore from "stores/InAppStudioStore";
import ActiveProgramDictionary from "stores/dictionary/ActiveProgramDictionary";
import FormControl, {
  TextFieldControl,
  DateFieldControl,
  CheckboxGroupWithSwitchFieldControl,
  SwitchFieldControl,
  UploadPreviewFieldControl,
  DropdownFieldControl,
  RadioButtonGroupControl,
} from "components/FormControl";
import SubmitButtonControl from "components/FormControl/SubmitButtonControl";
import LoadingButton from "components/LoadingButton";
import Row from "components/Form/Row";
import Loader from "components/Layout/Loader";
import PublishStatus from "components/PublishStatus";
import Popup from "components/Popup";
import ConfirmationDialog from "components/ConfirmationDialog";
import {
  compose,
  lengthValidator,
  imageTypesValidator,
  fileSizeValidator,
  startDateDayOfWeekValidator,
  imageIsCorruptedValidator,
} from "utils/validators";
import { ACCEPTED_IMAGE_TYPES } from "constants/image";
import { PUBLISH_STATUSES, WORKOUT_STRUCTURE_TYPES } from "constants/models";
import {
  Container,
  TitleContainer,
  ControlRow,
  Buttons,
  ButtonContainer,
} from "./styles";
import WorkoutFamilyDictionary from "stores/dictionary/WorkoutFamilyDictionary";
import CollectionStore from "stores/CollectionStore";
import withGlobalPopup from "components/Popup/withGlobalPopup";
import { withGlobalSnackbar } from "services/snackbar/snackbarContext";

type FormSubmitData = {
  values: any;
};

type Props = {
  isLoading: boolean;
  onClose: () => void;
  onChangeIsDirty: (params: { dirty: boolean }) => void;
  onSubmit: (data: FormSubmitData) => void;
  initialValues?: any;
  isFormLoading?: boolean;
  setMessage: (message: string, type?: string) => void;
};

const MAX_FILE_SIZE_IN_MB = 10;

@observer
class CategoryForm extends React.Component<Props> {
  @observable updateConfirmationIsOpened: boolean = false;
  @observable previousWorkoutFamily: any = null;
  @observable collections: any[] = [];
  @observable
  selectedWorkoutFamily: any = null;

  @computed get structureType() {
    return InAppStudioStore.categoryDetails?.formData?.structureType;
  }

  _isStructureTypeDisabled(type: string, negated = false): boolean {
    const selectedType = this.selectedWorkoutFamily?.structureType ?? "";
    const currentType = this.structureType ?? "";

    return (
      (negated
        ? !!selectedType && selectedType !== type
        : !!selectedType && selectedType === type) ||
      (negated
        ? !!currentType && currentType !== type
        : !!currentType && currentType === type)
    );
  }

  @computed get isProgramsDisabled() {
    return this._isStructureTypeDisabled(WORKOUT_STRUCTURE_TYPES.COLLECTION);
  }

  @computed get isCollectionsDisabled() {
    return this._isStructureTypeDisabled(
      WORKOUT_STRUCTURE_TYPES.COLLECTION,
      true,
    );
  }

  @computed get isWeekStructureDisabled() {
    return this._isStructureTypeDisabled(WORKOUT_STRUCTURE_TYPES.WEEK, true);
  }

  get isEditMode() {
    const { initialValues } = this.props;
    return !!initialValues;
  }

  get formTitle() {
    return !this.isEditMode ? "Add Category" : "Edit Category";
  }

  @computed get activeProgramsOptions() {
    return ActiveProgramDictionary.options.map(({ value, label }) => ({
      value: { id: value },
      label,
    }));
  }

  fetchCollections = async () => {
    try {
      const collections = await CollectionStore.getAllCollections();
      return collections;
    } catch (error) {
      this.props.setMessage("Failed to get collections", "error");
      console.error("Error fetching collections:", error);
      return [];
    }
  };

  @computed get personalizedCollectionsOptions() {
    return this.collections.map(collection => ({
      value: collection.id.toString(),
      label: collection.name,
    }));
  }

  @computed get initialValues() {
    return this.isEditMode ? InAppStudioStore.categoryDetails?.formData : {};
  }

  @computed get publishStatus() {
    return InAppStudioStore.categoryDetails?.formData?.publishStatus;
  }

  @computed get getWorkoutFamilyOptions() {
    return [...WorkoutFamilyDictionary.options];
  }

  @computed get nonStandardWorkoutFamilyOptions() {
    const withReducedFamilies = WorkoutFamilyDictionary.options.filter(
      e =>
        e.value.structureType !== WORKOUT_STRUCTURE_TYPES.WEEK &&
        e.value.structureType !== WORKOUT_STRUCTURE_TYPES.COLLECTION,
    );

    return [...withReducedFamilies];
  }

  @computed get workoutFamilyDropdownOptions() {
    const values = InAppStudioStore.categoryDetails?.formData;
    const isNotStandard =
      values?.structureType === WORKOUT_STRUCTURE_TYPES.WEEK ||
      values?.structureType === WORKOUT_STRUCTURE_TYPES.COLLECTION;

    if (!this.isEditMode) {
      return this.getWorkoutFamilyOptions;
    }

    return isNotStandard && this.isEditMode
      ? this.getWorkoutFamilyOptions
      : this.nonStandardWorkoutFamilyOptions;
  }

  async componentDidMount() {
    const { initialValues } = this.props;
    await WorkoutFamilyDictionary.fetch();

    const collections = await CollectionStore.getAllCollections();
    this.collections = collections.data;

    if (initialValues?.id) {
      await InAppStudioStore.fetchCategoryDetails(initialValues.id);
      await InAppStudioStore.fetchCardCategoryImage(initialValues.id);
    }
  }

  componentWillUnmount() {
    InAppStudioStore.resetCategoryDetails();
  }

  formFileValidator = (acceptedTypes: string) => (value?: File) =>
    imageTypesValidator(value?.type ?? "", acceptedTypes);

  openUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = true;
  };

  closeUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = false;
  };

  confirmUpdate = (values: any) => () => {
    const { onSubmit } = this.props;

    this.closeUpdateConfirmation();
    onSubmit(values);
  };

  onSubmit = (values: any) => {
    const { onSubmit } = this.props;

    const shouldShowUpdateConfirmation =
      this.publishStatus &&
      [
        PUBLISH_STATUSES.PUBLISHED,
        PUBLISH_STATUSES.SCHEDULE_FOR_REMOVE,
      ].includes(this.publishStatus);

    shouldShowUpdateConfirmation
      ? this.openUpdateConfirmation()
      : onSubmit(values);
  };

  @action
  onWorkoutFamilyChange = (value: any, previousValue: any) => {
    this.previousWorkoutFamily = previousValue;
    this.selectedWorkoutFamily = value;
  };

  render() {
    const { onClose, isFormLoading, onChangeIsDirty } = this.props;

    return (
      <FormControl onSubmit={this.onSubmit} initialValues={this.initialValues}>
        {this.updateConfirmationIsOpened && (
          <FormSpy subscription={{ values: true }}>
            {({ values }) => (
              <Popup onClose={this.closeUpdateConfirmation} withoutExitButton>
                <ConfirmationDialog
                  title="Save Changes to Production"
                  text="All changes made will be published to production. Are you sure you want to proceed?"
                  acceptText="Proceed"
                  declineText="Cancel"
                  onAccept={this.confirmUpdate(values)}
                  onDecline={this.closeUpdateConfirmation}
                />
              </Popup>
            )}
          </FormSpy>
        )}
        <FormSpy subscription={{ dirty: true }} onChange={onChangeIsDirty} />
        <Container>
          <TitleContainer>
            <Typography variant="h1" color="textPrimary">
              {this.formTitle}
            </Typography>
            {this.isEditMode && this.publishStatus && (
              <PublishStatus status={this.publishStatus} />
            )}
          </TitleContainer>

          {!isFormLoading ? (
            <>
              <Row>
                <TextFieldControl
                  variant="outlined"
                  margin="none"
                  label="Title"
                  name="title"
                  placeholder="Enter title"
                  validate={lengthValidator(100)}
                  fullWidth
                  required
                />
              </Row>

              <Row>
                <TextFieldControl
                  variant="outlined"
                  margin="none"
                  name="subTitle"
                  label="Description"
                  placeholder="Enter description"
                  validate={lengthValidator(10000)}
                  minRows={5}
                  maxRows={10}
                  fullWidth
                  multiline
                />
              </Row>

              <Row>
                <TextFieldControl
                  variant="outlined"
                  margin="none"
                  name="note"
                  label="Note"
                  placeholder="Enter note"
                  validate={lengthValidator(256)}
                  fullWidth
                />
              </Row>

              <Row>
                {this.isEditMode ? (
                  <FormSpy subscription={{ values: true }}>
                    {({ values }) => {
                      const isNotStandard =
                        values.structureType === WORKOUT_STRUCTURE_TYPES.WEEK ||
                        values.structureType ===
                          WORKOUT_STRUCTURE_TYPES.COLLECTION;

                      return (
                        <DropdownFieldControl
                          name="family"
                          options={this.workoutFamilyDropdownOptions}
                          onChangeCallback={this.onWorkoutFamilyChange}
                          label="Workout Families"
                          disabled={isNotStandard}
                          isClearable
                          required
                        />
                      );
                    }}
                  </FormSpy>
                ) : (
                  <DropdownFieldControl
                    name="family"
                    options={this.getWorkoutFamilyOptions}
                    onChangeCallback={this.onWorkoutFamilyChange}
                    label="Workout Families"
                    isClearable
                    required
                  />
                )}
              </Row>

              <Row>
                <UploadPreviewFieldControl
                  name="file"
                  title="Image"
                  acceptedFiles={ACCEPTED_IMAGE_TYPES}
                  validate={compose(
                    this.formFileValidator(ACCEPTED_IMAGE_TYPES),
                    fileSizeValidator(MAX_FILE_SIZE_IN_MB),
                    imageIsCorruptedValidator,
                  )}
                  required
                />
              </Row>

              <Row>
                <UploadPreviewFieldControl
                  name="cardFile"
                  title="Card Image"
                  acceptedFiles={ACCEPTED_IMAGE_TYPES}
                  validate={compose(
                    this.formFileValidator(ACCEPTED_IMAGE_TYPES),
                    fileSizeValidator(MAX_FILE_SIZE_IN_MB),
                  )}
                  required={false}
                />
              </Row>

              {!this.isProgramsDisabled && (
                <ControlRow>
                  <CheckboxGroupWithSwitchFieldControl
                    name="activePrograms"
                    title="Access"
                    options={this.activeProgramsOptions}
                    switchLabel="Preview only"
                    disabled={this.isProgramsDisabled}
                    required={!this.isProgramsDisabled}
                    inline
                    multi
                  />
                </ControlRow>
              )}

              {!this.isCollectionsDisabled && (
                <ControlRow>
                  <RadioButtonGroupControl
                    name="collection"
                    label="Add to Special Program"
                    options={this.personalizedCollectionsOptions}
                    disabled={this.isCollectionsDisabled}
                    required={!this.isCollectionsDisabled}
                    inline
                  />
                </ControlRow>
              )}

              <FormSpy subscription={{ values: true }}>
                {({ values }) => (
                  <>
                    {!this.isWeekStructureDisabled && (
                      <ControlRow>
                        <SwitchFieldControl
                          name="isWeekStructureEnabled"
                          title="Settings"
                          label="Enable week structure"
                          color="primary"
                          size="small"
                          disabled={this.isWeekStructureDisabled}
                          checked
                        />
                      </ControlRow>
                    )}
                    {!this.isWeekStructureDisabled && (
                      <Row>
                        <DateFieldControl
                          name="startDate"
                          title="Start date"
                          inputVariant="outlined"
                          margin="none"
                          validate={startDateDayOfWeekValidator(1)}
                          fullWidth
                          required
                        />
                      </Row>
                    )}
                  </>
                )}
              </FormSpy>
            </>
          ) : (
            <Row>
              <Loader />
            </Row>
          )}
        </Container>

        <Buttons>
          <ButtonContainer>
            <Button
              variant="outlined"
              color="primary"
              onClick={onClose}
              fullWidth
            >
              Cancel
            </Button>
          </ButtonContainer>
          <ButtonContainer>
            <SubmitButtonControl render={this.renderButton} />
          </ButtonContainer>
        </Buttons>
      </FormControl>
    );
  }

  renderButton = ({ onClick }: { onClick: () => void }) => (
    <LoadingButton
      onClick={onClick}
      variant="contained"
      color="primary"
      isLoading={this.props.isLoading}
      fullWidth
    >
      {!this.isEditMode ? "Add" : "Save"}
    </LoadingButton>
  );
}

export default withGlobalPopup(withGlobalSnackbar(CategoryForm));
